import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  Users, Building2, Search, UserPlus, Check, X, 
  MessageSquare, Crown, ArrowLeft, Loader2, AlertCircle,
  ExternalLink, DollarSign, Store, Package, MessageCircle, Send, Bell, Sparkles
} from 'lucide-react';
import Footer from '../components/Footer';
import NotifBell from '../components/NotifBell';
import { 
  showChatNotification, 
  hasNotificationPermission
} from '../services/notifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CollaborationsPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [collaborations, setCollaborations] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [affiliateBrands, setAffiliateBrands] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [affiliateSearchQuery, setAffiliateSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  
  // Chat state
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPartner, setChatPartner] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const chatEndRef = useRef(null);

  const [chatCollabStatus, setChatCollabStatus] = useState(null);
  const chatCollabStatusRef = useRef(null);

  // Per-collab unread message counts
  const [unreadPerCollab, setUnreadPerCollab] = useState({});

  // Notification state from global context
  const { counts: globalNotifCounts, markViewed, setOnCountsChange } = useNotifications();
  const pendingInviteCount = globalNotifCounts?.pending_collab_invites || 0;
  const lastMessageCountRef = useRef(0);

  const isBrand = user?.user_type === 'brand';
  const isInfluencer = user?.user_type === 'influencer';
  const canSendCollabRequests = planLimits?.can_send_collab_requests;

  // Check if user is on Pro plan
  const isProPlan = user?.plan_id === 'brand_enterprise' || user?.plan_id === 'influencer_pro';

  // Fetch notification counts for collab invites - MUST be defined before useEffect that uses it
  // (Now handled by global NotificationContext)

  // Mark collab notifications as viewed when user opens pending tab - MUST be defined before useEffect that uses it
  // (Now uses global markViewed from NotificationContext)

  // Check notification permission on mount - handled by global NotificationPrompt
  useEffect(() => {}, []);

  // Fetch per-collab unread message counts
  const fetchUnreadPerCollab = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/unread-per-collab`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadPerCollab(data.unread_counts || {});
      }
    } catch (err) {
      console.error('Error fetching unread per collab:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token && user) {
      fetchUnreadPerCollab();
      const interval = setInterval(fetchUnreadPerCollab, 15000);
      return () => clearInterval(interval);
    }
  }, [token, user, fetchUnreadPerCollab]);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Mark notifications as viewed when pending tab is opened
  useEffect(() => {
    if (activeTab === 'pending' && pendingInviteCount > 0) {
      markViewed();
    }
  }, [activeTab, pendingInviteCount, markViewed]);

  // Fetch collaborations
  const fetchCollaborations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/collaborations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCollaborations(data.collaborations || []);
      }
    } catch (err) {
      console.error('Error fetching collaborations:', err);
    }
  }, [token]);

  // Fetch plan limits
  const fetchPlanLimits = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/plan-limits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlanLimits(data);
      }
    } catch (err) {
      console.error('Error fetching plan limits:', err);
    }
  }, [token]);

  // Fetch available partners for collaboration
  const fetchAvailablePartners = useCallback(async () => {
    const endpoint = isBrand 
      ? '/api/collaborations/available-influencers'
      : '/api/collaborations/available-brands';
    
    try {
      const res = await fetch(`${API_URL}${endpoint}?search=${searchQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailablePartners(data.influencers || data.brands || []);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  }, [token, isBrand, searchQuery]);

  // Fetch brands for affiliate
  const fetchAffiliateBrands = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/collaborations/browse-brands-affiliate?search=${affiliateSearchQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAffiliateBrands(data.brands || []);
      }
    } catch (err) {
      console.error('Error fetching affiliate brands:', err);
    }
  }, [token, affiliateSearchQuery]);

  // Fetch chat messages
  const hasLoadedChatRef = useRef(false);
  const fetchChatMessages = useCallback(async (collaborationId, isPolling = false) => {
    if (!isPolling && !hasLoadedChatRef.current) setLoadingChat(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/${collaborationId}?_t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      if (res.ok) {
        const data = await res.json();
        const newMessages = data.messages || [];
        
        if (isPolling && hasNotificationPermission() && newMessages.length > lastMessageCountRef.current) {
          const lastKnownCount = lastMessageCountRef.current;
          const newIncomingMessages = newMessages.slice(lastKnownCount).filter(
            msg => msg.sender_id !== user?.id
          );
          
          if (newIncomingMessages.length > 0 && data.partner) {
            const latestMessage = newIncomingMessages[newIncomingMessages.length - 1];
            showChatNotification(
              data.partner.name || 'Partner',
              latestMessage.message || '',
              '/collaborations'
            );
          }
        }
        
        lastMessageCountRef.current = newMessages.length;
        hasLoadedChatRef.current = true;
        setChatMessages(newMessages);
        setChatPartner(data.partner);
        
        if (data.collaboration_status === 'ended' && chatCollabStatusRef.current !== 'ended') {
          chatCollabStatusRef.current = 'ended';
          setChatCollabStatus('ended');
        } else {
          chatCollabStatusRef.current = data.collaboration_status;
          setChatCollabStatus(data.collaboration_status);
        }
        
        fetch(`${API_URL}/api/chat/${collaborationId}/mark-read`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Error fetching chat messages:', err);
    } finally {
      setLoadingChat(false);
    }
  }, [token, user?.id]);

  // Initial load
  useEffect(() => {
    if (token && user) {
      Promise.all([
        fetchCollaborations(),
        fetchPlanLimits()
      ]).finally(() => setLoading(false));
    }
  }, [token, user, fetchCollaborations, fetchPlanLimits]);

  // Live notification-driven data refresh
  useEffect(() => {
    if (!setOnCountsChange) return;
    setOnCountsChange(() => {
      fetchCollaborations();
      fetchUnreadPerCollab();
    });
    return () => setOnCountsChange(null);
  }, [setOnCountsChange, fetchCollaborations, fetchUnreadPerCollab]);

  // Fetch partners when browse tab is active
  useEffect(() => {
    if (activeTab === 'browse' && token && (isBrand || canSendCollabRequests)) {
      fetchAvailablePartners();
    }
  }, [activeTab, searchQuery, token, fetchAvailablePartners, isBrand, canSendCollabRequests]);

  // Fetch affiliate brands when affiliate tab is active
  useEffect(() => {
    if (activeTab === 'affiliate' && token && isInfluencer) {
      fetchAffiliateBrands();
    }
  }, [activeTab, affiliateSearchQuery, token, fetchAffiliateBrands, isInfluencer]);

  // Fetch chat when selected - with visibility-aware polling
  const pollingRef = useRef(false);
  useEffect(() => {
    if (!selectedChat) return;
    
    lastMessageCountRef.current = 0;
    hasLoadedChatRef.current = false;
    fetchChatMessages(selectedChat, false);
    
    // Non-stacking poll: waits for previous fetch to finish before scheduling next
    let timeoutId = null;
    let active = true;
    
    const poll = async () => {
      if (!active || pollingRef.current) return;
      pollingRef.current = true;
      try {
        await fetchChatMessages(selectedChat, true);
      } finally {
        pollingRef.current = false;
        if (active) timeoutId = setTimeout(poll, 4000);
      }
    };
    timeoutId = setTimeout(poll, 4000);
    
    // Force refresh when tab becomes visible (mobile resumes from background)
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && active) {
        fetchChatMessages(selectedChat, true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    
    return () => {
      active = false;
      pollingRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selectedChat, fetchChatMessages]);

  // Send collaboration invite
  const handleSendInvite = async (handle) => {
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/collaborations/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          influencer_handle: handle,
          message: ''
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSuccess('Collaboration invite sent successfully!');
        fetchCollaborations();
        fetchAvailablePartners();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        try {
          const data = await res.json();
          setError(data.detail || 'Failed to send invitation');
        } catch {
          if (res.status === 403) {
            setError("You've reached your connection limit. Please upgrade your plan.");
          } else {
            setError(`Request failed (${res.status}). Please try again.`);
          }
        }
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  // Respond to collaboration
  const handleRespond = async (collabId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/collaborations/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          collaboration_id: collabId,
          action: action
        })
      });
      
      if (res.ok) {
        fetchCollaborations();
        setSuccess(`Collaboration ${action}ed!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        try {
          const data = await res.json();
          setError(data.detail || `Failed to ${action} collaboration`);
        } catch {
          setError(`Failed to ${action} collaboration. Please try again.`);
        }
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    }
  };

  // End collaboration
  const handleEndCollaboration = async (collabId) => {
    if (!window.confirm('Are you sure you want to end this collaboration?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/collaborations/${collabId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchCollaborations();
        setSelectedChat(null);
        setSuccess('Collaboration ended');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to end collaboration');
    }
  };

  // Send chat message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;
    
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          collaboration_id: selectedChat,
          message: newMessage.trim()
        })
      });
      
      if (res.ok) {
        setNewMessage('');
        fetchChatMessages(selectedChat, false);
      }
    } catch (err) {
      setError('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // View portfolio
  const handleViewPortfolio = (handle, type) => {
    if (type === 'brand') {
      navigate(`/profile/${handle}`);
    } else {
      navigate(`/influencer/${handle}`);
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Not authorized
  if (!user || (user.user_type !== 'brand' && user.user_type !== 'influencer')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Collaborations are for Brands & Influencers</h2>
          <p className="text-gray-500 mt-2">Upgrade your plan to access collaboration features.</p>
        </div>
      </div>
    );
  }

  const activeCollabs = collaborations.filter(c => c.status === 'accepted' || c.status === 'ended');
  const pendingCollabs = collaborations.filter(c => c.status === 'pending');
  const showBrowseTab = isBrand || canSendCollabRequests;
  const showAffiliateTab = isInfluencer;

  // Chat View
  if (selectedChat) {
    const currentCollab = activeCollabs.find(c => c.id === selectedChat);
    const isEnded = currentCollab?.status === 'ended' || chatCollabStatus === 'ended';
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex flex-col">
        {/* Chat Header - Fixed on mobile */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50 safe-area-top">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => setSelectedChat(null)} 
                className="flex items-center text-gray-600 hover:text-gray-800 min-w-0"
                data-testid="back-to-collabs"
              >
                <ArrowLeft className="w-5 h-5 flex-shrink-0" />
                <span className="ml-2 hidden sm:inline">Back to Collaborations</span>
                <span className="ml-2 sm:hidden">Back</span>
              </button>
              {/* Logo + Bell */}
              <div className="flex items-center space-x-3">
                <NotifBell />
                <div className="flex items-center space-x-2 cursor-default">
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />
                  <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
                    VirtualFit
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Chat Container - Flex grow to fill available space */}
        <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-3 sm:px-4 py-3 sm:py-6 overflow-hidden">
          {/* Chat Partner Info Header */}
          <div className="bg-white rounded-t-2xl p-3 sm:p-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {chatPartner?.profile_image ? (
                    <img src={chatPartner.profile_image.startsWith('data:') ? chatPartner.profile_image : `data:image/jpeg;base64,${chatPartner.profile_image}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    chatPartner?.user_type === 'brand' 
                      ? <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />
                      : <Users className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400" />
                  )}
                </div>
                <div className="ml-3 min-w-0">
                  <h3 className="font-semibold text-gray-800 truncate">{chatPartner?.name || 'Partner'}</h3>
                  <p className="text-sm text-gray-500 truncate">@{chatPartner?.handle}</p>
                </div>
              </div>
              {/* Action buttons - Hidden on mobile, shown on sm+ */}
              <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-2">
                <button
                  onClick={() => handleViewPortfolio(chatPartner?.handle, chatPartner?.user_type)}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center"
                  data-testid="view-partner-portfolio"
                >
                  <Package className="w-4 h-4 mr-1" />
                  Portfolio
                </button>
                {!isEnded && (
                  <button
                    onClick={() => handleEndCollaboration(selectedChat)}
                    className="px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid="end-collab-chat"
                  >
                    End
                  </button>
                )}
              </div>
              {/* Mobile menu button - includes End action */}
              <div className="sm:hidden flex-shrink-0 ml-2 flex items-center gap-1">
                {!isEnded && (
                  <button
                    onClick={() => handleEndCollaboration(selectedChat)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid="end-collab-mobile"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleViewPortfolio(chatPartner?.handle, chatPartner?.user_type)}
                  className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  data-testid="view-partner-portfolio-mobile"
                >
                  <Package className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Chat Messages - Scrollable area */}
          <div className="bg-white flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 min-h-0" data-testid="chat-messages">
            {loadingChat ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageCircle className="w-12 h-12 mb-2" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              chatMessages.map(msg => (
                <div key={msg.id} className={`flex ${msg.is_system ? 'justify-center' : msg.is_mine ? 'justify-end' : 'justify-start'}`}>
                  {msg.is_system ? (
                    <div className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-500 italic" data-testid="system-message">
                      {msg.message}
                    </div>
                  ) : (
                    <div className={`max-w-[85%] sm:max-w-[70%] px-3 sm:px-4 py-2 rounded-2xl ${
                      msg.is_mine 
                        ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <p className="break-words">{msg.message}</p>
                      <p className={`text-xs mt-1 ${msg.is_mine ? 'text-white/70' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat Input - Fixed at bottom */}
          {isEnded ? (
            <div className="bg-gray-50 rounded-b-2xl p-4 border-t text-center safe-area-bottom">
              <p className="text-gray-500 text-sm">This collaboration has ended. You can no longer send messages.</p>
            </div>
          ) : (
            <form onSubmit={handleSendMessage} className="bg-white rounded-b-2xl p-3 sm:p-4 border-t flex-shrink-0 safe-area-bottom">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-full focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
                  data-testid="chat-input"
                />
                <button
                  type="submit"
                  disabled={sendingMessage || !newMessage.trim()}
                  className="p-2.5 sm:p-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full hover:shadow-lg disabled:opacity-50 flex-shrink-0"
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer - Hidden on mobile in chat view for more space */}
        <div className="hidden sm:block">
          <Footer isAuthenticated={true} isProPlan={isProPlan} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center text-gray-600 hover:text-gray-800"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center space-x-4">
            {/* Notification Bell */}
            <NotifBell />
            {/* Logo */}
            <div className="flex items-center space-x-2 cursor-default">
              <Sparkles className="w-6 h-6 text-rose-400" />
              <span className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
                VirtualFit
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 flex-1">
        {/* Notification Permission Banner */}
        {/* Title & Stats */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="page-title">
              {isBrand ? 'Influencer Collaborations' : 'Brands & Collaborations'}
            </h1>
            <p className="text-gray-600">
              {isBrand 
                ? 'Connect with influencers to showcase your clothing' 
                : canSendCollabRequests 
                  ? 'Partner with brands for exclusive try-on content'
                  : 'Browse brands and join paid campaigns'}
            </p>
          </div>
          
          {/* Plan Limits for Brands */}
          {planLimits && isBrand && (
            <div className="mt-4 md:mt-0 bg-white rounded-xl px-6 py-4 shadow-sm" data-testid="connection-stats">
              <p className="text-sm text-gray-500">Connections</p>
              <p className={`text-2xl font-bold ${
                planLimits.max_influencer_connections !== 'Unlimited' && planLimits.current_connections >= planLimits.max_influencer_connections
                  ? 'text-red-500' : 'text-gray-800'
              }`}>
                {planLimits.current_connections} / {planLimits.max_influencer_connections}
              </p>
              <p className="text-xs text-gray-400">pending + active</p>
            </div>
          )}

          {/* Plan Limits for Influencers */}
          {planLimits && isInfluencer && canSendCollabRequests && (
            <div className="mt-4 md:mt-0 bg-white rounded-xl px-6 py-4 shadow-sm" data-testid="collab-request-stats">
              <p className="text-sm text-gray-500">Collab Requests</p>
              <p className={`text-2xl font-bold ${
                planLimits.max_collab_requests !== 'Unlimited' && planLimits.collab_requests_sent >= planLimits.max_collab_requests
                  ? 'text-red-500' : 'text-gray-800'
              }`}>
                {planLimits.collab_requests_sent} / {planLimits.max_collab_requests}
              </p>
              <p className="text-xs text-gray-400">pending + active</p>
            </div>
          )}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center text-green-700" data-testid="success-message">
            <Check className="w-5 h-5 mr-2" />
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4" data-testid="error-message">
            {(error.toLowerCase().includes('limit') || error.toLowerCase().includes('upgrade')) ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <span className="font-semibold">Limit Reached</span>
                  <button onClick={() => setError('')} className="ml-auto text-amber-500 hover:text-amber-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm mb-3">{error}</p>
                <Link
                  to="/subscription"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all"
                  data-testid="collab-upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Link>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center text-red-700">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
                <button onClick={() => setError('')} className="ml-auto">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('active')}
            data-testid="tab-active"
            className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'active' 
                ? 'text-rose-500 border-b-2 border-rose-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Active ({activeCollabs.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending"
            className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap relative ${
              activeTab === 'pending' 
                ? 'text-rose-500 border-b-2 border-rose-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Pending ({pendingCollabs.length})
            {pendingInviteCount > 0 && activeTab !== 'pending' && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="pending-invite-badge">
                {pendingInviteCount}
              </span>
            )}
          </button>
          
          {showBrowseTab && (
            <button
              onClick={() => setActiveTab('browse')}
              data-testid="tab-browse"
              className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'browse' 
                  ? 'text-rose-500 border-b-2 border-rose-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {isBrand ? 'Find Influencers' : 'Send Collab Request'}
            </button>
          )}

          {showAffiliateTab && (
            <button
              onClick={() => setActiveTab('affiliate')}
              data-testid="tab-affiliate"
              className={`pb-4 px-2 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'affiliate' 
                  ? 'text-rose-500 border-b-2 border-rose-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                Find Brands
              </span>
            </button>
          )}
        </div>

        {/* Active Collaborations Tab */}
        {activeTab === 'active' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="active-collabs-grid">
            {activeCollabs.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No active collaborations yet</p>
                {(isBrand || canSendCollabRequests) && (
                  <button
                    onClick={() => setActiveTab('browse')}
                    className="mt-4 px-6 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg"
                    data-testid="find-partners-btn"
                  >
                    Find {isBrand ? 'Influencers' : 'Brands'}
                  </button>
                )}
              </div>
            ) : (
              activeCollabs.map(collab => (
                <div key={collab.id} className="bg-white rounded-2xl p-6 shadow-lg" data-testid={`collab-${collab.id}`}>
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden">
                      {collab.partner_image ? (
                        <img src={collab.partner_image.startsWith('data:') ? collab.partner_image : `data:image/jpeg;base64,${collab.partner_image}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        collab.partner_type === 'brand' 
                          ? <Building2 className="w-7 h-7 text-rose-400" />
                          : <Users className="w-7 h-7 text-rose-400" />
                      )}
                    </div>
                    <div className="ml-4">
                      <h3 className="font-semibold text-gray-800">{collab.partner_name}</h3>
                      <p className="text-sm text-gray-500">@{collab.partner_handle}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-4">
                    {collab.status === 'ended' ? (
                      <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-sm">Ended{collab.ended_by_name ? ` by ${collab.ended_by_name}` : ''}</span>
                    ) : (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Active</span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedChat(collab.id);
                        setChatPartner({
                          name: collab.partner_name,
                          handle: collab.partner_handle,
                          profile_image: collab.partner_image,
                          user_type: collab.partner_type
                        });
                      }}
                      className={`flex-1 py-2 ${collab.status === 'ended' ? 'bg-gray-200 text-gray-600' : 'bg-gradient-to-r from-rose-500 to-orange-500 text-white hover:shadow-lg'} rounded-xl flex items-center justify-center relative`}
                      data-testid={`chat-${collab.id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {collab.status === 'ended' ? 'View Chat' : 'Chat'}
                      {unreadPerCollab[collab.id] > 0 && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse" data-testid={`unread-badge-${collab.id}`}>
                          {unreadPerCollab[collab.id] > 9 ? '9+' : unreadPerCollab[collab.id]}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => handleViewPortfolio(collab.partner_handle, collab.partner_type)}
                      className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl flex items-center justify-center"
                      data-testid={`portfolio-${collab.id}`}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Portfolio
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pending Collaborations Tab */}
        {activeTab === 'pending' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pending-collabs-grid">
            {pendingCollabs.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No pending invitations</p>
              </div>
            ) : (
              pendingCollabs.map(collab => {
                const isReceived = collab.initiated_by !== user.user_type;
                return (
                  <div key={collab.id} className="bg-white rounded-2xl p-6 shadow-lg" data-testid={`pending-collab-${collab.id}`}>
                    <div className="flex items-center mb-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden">
                        {collab.partner_image ? (
                          <img src={collab.partner_image.startsWith('data:') ? collab.partner_image : `data:image/jpeg;base64,${collab.partner_image}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          collab.partner_type === 'brand' 
                            ? <Building2 className="w-7 h-7 text-rose-400" />
                            : <Users className="w-7 h-7 text-rose-400" />
                        )}
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-gray-800">{collab.partner_name}</h3>
                        <p className="text-sm text-gray-500">@{collab.partner_handle}</p>
                      </div>
                    </div>
                    
                    {/* View Portfolio Button */}
                    <button
                      onClick={() => handleViewPortfolio(collab.partner_handle, collab.partner_type)}
                      className="w-full mb-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl flex items-center justify-center text-sm"
                      data-testid={`view-portfolio-pending-${collab.id}`}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      View Portfolio
                    </button>
                    
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                        {isReceived ? 'Received' : 'Sent'}
                      </span>
                      
                      {isReceived ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRespond(collab.id, 'accept')}
                            className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                            data-testid={`accept-collab-${collab.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRespond(collab.id, 'decline')}
                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            data-testid={`decline-collab-${collab.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEndCollaboration(collab.id)}
                          className="text-gray-400 hover:text-red-500 text-sm"
                          data-testid={`cancel-collab-${collab.id}`}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Browse Partners Tab */}
        {activeTab === 'browse' && showBrowseTab && (
          <div data-testid="browse-partners-section">
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${isBrand ? 'influencers' : 'brands'}...`}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                data-testid="search-partners-input"
              />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availablePartners.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No results found' : `No ${isBrand ? 'influencers' : 'brands'} available`}
                  </p>
                </div>
              ) : (
                availablePartners.map(partner => (
                  <div key={partner.handle} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow" data-testid={`partner-${partner.handle}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden">
                          {partner.profile_image ? (
                            <img src={partner.profile_image.startsWith('data:') ? partner.profile_image : `data:image/jpeg;base64,${partner.profile_image}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            isBrand 
                              ? <Users className="w-7 h-7 text-rose-400" />
                              : <Building2 className="w-7 h-7 text-rose-400" />
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <h3 className="font-semibold text-gray-800">{partner.name}</h3>
                            {partner.is_pro && (
                              <Crown className="w-4 h-4 text-amber-500 ml-2" title="Pro Influencer" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500">@{partner.handle}</p>
                        </div>
                      </div>
                    </div>
                    
                    {partner.bio && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3 break-all">{partner.bio}</p>
                    )}
                    
                    {/* View Portfolio Button */}
                    <button
                      onClick={() => handleViewPortfolio(partner.handle, isBrand ? 'influencer' : 'brand')}
                      className="w-full mb-2 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl flex items-center justify-center"
                      data-testid={`view-portfolio-${partner.handle}`}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      View Portfolio
                    </button>
                    
                    <button
                      onClick={() => handleSendInvite(partner.handle)}
                      disabled={sending}
                      className="w-full py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg flex items-center justify-center disabled:opacity-50"
                      data-testid={`invite-${partner.handle}`}
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Invite to Collaborate
                        </>
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Affiliate Brands Tab */}
        {activeTab === 'affiliate' && showAffiliateTab && (
          <div data-testid="affiliate-brands-section">
            {/* Info Banner */}
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
              <div className="flex items-start">
                <DollarSign className="w-6 h-6 text-blue-500 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Earn Through Brand Campaigns</h3>
                  <p className="text-sm text-gray-600">
                    Browse brands below and use their clothing in your try-on content. Join brand campaigns to earn money, or add your own store link to direct followers to your shop.
                  </p>
                  <Link 
                    to="/settings" 
                    className="inline-flex items-center mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Set up your store link <ExternalLink className="w-3 h-3 ml-1" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={affiliateSearchQuery}
                onChange={(e) => setAffiliateSearchQuery(e.target.value)}
                placeholder="Search brands..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                data-testid="search-affiliate-brands-input"
              />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {affiliateBrands.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {affiliateSearchQuery ? 'No brands found' : 'No brands available yet'}
                  </p>
                </div>
              ) : (
                affiliateBrands.map(brand => (
                  <div key={brand.handle} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow" data-testid={`affiliate-brand-${brand.handle}`}>
                    <div className="flex items-center mb-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden">
                        {brand.profile_image ? (
                          <img src={brand.profile_image.startsWith('data:') ? brand.profile_image : `data:image/jpeg;base64,${brand.profile_image}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Building2 className="w-7 h-7 text-rose-400" />
                        )}
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-gray-800">{brand.name}</h3>
                        <p className="text-sm text-gray-500">@{brand.handle}</p>
                      </div>
                    </div>
                    
                    {brand.bio && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-3 break-all">{brand.bio}</p>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>{brand.portfolio_count || 0} clothing items</span>
                      {brand.website && (
                        <a 
                          href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-600 flex items-center"
                        >
                          Website <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleViewPortfolio(brand.handle, 'brand')}
                      className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl flex items-center justify-center transition-colors"
                      data-testid={`view-portfolio-${brand.handle}`}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      View Portfolio
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Upgrade Prompt for Starter Influencers */}
            {/* Upgrade Prompt for Starter Influencers - Only show for non-Pro users */}
            {!canSendCollabRequests && !isProPlan && (
              <div className="mt-8 bg-gradient-to-r from-purple-50 to-rose-50 rounded-2xl p-6 text-center">
                <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Want to Collaborate Directly?</h3>
                <p className="text-gray-600 mb-4">
                  Upgrade to <span className="font-semibold">Influencer Pro</span> to send collaboration requests to brands 
                  and get priority listing in search.
                </p>
                <Link
                  to="/subscription"
                  className="inline-block px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg font-medium"
                  data-testid="upgrade-to-pro-btn"
                >
                  Upgrade to Pro
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer isAuthenticated={true} isProPlan={isProPlan} />
    </div>
  );
}
